import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  AccountDto,
  AccountUpdateRequest,
  AccountsControllerService,
  ReferralAgentControllerService,
  ReferralAgentCreationRequest,
  ReferralAgentDto,
  ReferralAgentUpdateRequest,
} from '@api/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { isDefined } from '@stillmandigital/common';
import { ReferralAgentDetailsDialogComponent } from 'app/settings/components/referral-agents/referral-agent-details-dialog/referral-agent-details-dialog.component';
import { ReferralAgentActionType } from 'app/settings/store/referral-agents/referral-agents.interfaces';
import { pipe, switchMap } from 'rxjs';

export const useReferralAgents = () =>
  signalStoreFeature(
    {
      state: type<{ accounts: AccountDto[] }>(),
      methods: type<{
        loadAccounts: () => void;
      }>(),
      props: {
        _notificationService: type<NotificationService>(),
      },
    },
    withState({
      referralAgents: new Array<ReferralAgentDto>(),
      referralAgentsLoaded: false,
      selectedReferralAgent: undefined as ReferralAgentDto | undefined,
    }),
    withProps(() => ({
      _referralAgentService: inject(ReferralAgentControllerService),
      _accountsService: inject(AccountsControllerService),
      _dialogService: inject(MatDialog),
    })),
    withComputed(store => ({
      linkedAccounts: computed(() =>
        store.accounts().filter(account => account.referralAgentId === store.selectedReferralAgent()?.id),
      ),
      unlinkedAccounts: computed(() => store.accounts().filter(account => !isDefined(account.referralAgentId))),
    })),
    withMethods(store => {
      const loadReferralAgents = rxMethod<void>(
        pipe(
          switchMap(() =>
            store._referralAgentService.getReferralAgents().pipe(
              tapResponse({
                next: referralAgents => patchState(store, { referralAgents, referralAgentsLoaded: true }),
                error: (error: HttpErrorResponse) => {
                  store._notificationService.showErrorNotification(`The referral agents couldn't be loaded`, error);
                  patchState(store, { referralAgentsLoaded: true });
                },
              }),
            ),
          ),
        ),
      );

      const openReferralAgentDetailsDialog = (referralAgentActionType: ReferralAgentActionType) => {
        store._dialogService.open(ReferralAgentDetailsDialogComponent, {
          data: referralAgentActionType,
        });
      };

      const setSelectedReferralAgent = (selectedReferralAgent: ReferralAgentDto | undefined) => {
        patchState(store, { selectedReferralAgent });
      };

      const createReferralAgent = rxMethod<ReferralAgentCreationRequest>(
        pipe(
          switchMap(request =>
            store._referralAgentService.createReferralAgent({ referralAgentCreationRequest: request }).pipe(
              tapResponse({
                next: () => {
                  store._notificationService.showSuccessNotification(
                    'The referral agent has been created successfully',
                  );
                  loadReferralAgents();
                },
                error: (error: HttpErrorResponse) =>
                  store._notificationService.showErrorNotification(`The referral agent couldn't be created`, error),
              }),
            ),
          ),
        ),
      );

      const updateReferralAgent = rxMethod<ReferralAgentUpdateRequest>(
        pipe(
          switchMap(request =>
            store._referralAgentService.updateReferralAgent({ referralAgentUpdateRequest: request }).pipe(
              tapResponse({
                next: () => {
                  store._notificationService.showSuccessNotification(
                    'The referral agent has been updated successfully',
                  );
                  loadReferralAgents();
                },
                error: (error: HttpErrorResponse) =>
                  store._notificationService.showErrorNotification(`The referral agent couldn't be updated`, error),
              }),
            ),
          ),
        ),
      );

      const updateAccount = rxMethod<AccountUpdateRequest>(
        pipe(
          switchMap(account =>
            store._accountsService.updateAccount({ accountUpdateRequest: account }).pipe(
              tapResponse({
                next: () => {
                  store._notificationService.showSuccessNotification('The account has been updated successfully');
                  loadReferralAgents();
                  store.loadAccounts();
                },
                error: (error: HttpErrorResponse) =>
                  store._notificationService.showErrorNotification(`The account couldn't be updated`, error),
              }),
            ),
          ),
        ),
      );

      return {
        loadReferralAgents,
        setSelectedReferralAgent,
        openReferralAgentDetailsDialog,
        createReferralAgent,
        updateReferralAgent,
        updateAccount,
      };
    }),
  );
